import { z } from 'zod';
export const MCPTransportSchema = z.enum(['sse', 'http']);
/**
 * v1 ships only NONE + OAUTH. The enum is polymorphic so future flows can be
 * added without a follow-up migration.
 */
export const MCPAuthTypeSchema = z.enum(['none', 'oauth']);
/**
 * Per-connection OAuth configuration (catalog rows carry it; org-created rows
 * may discover it from `endpoint`'s well-known metadata at OAuth start time).
 * Stored as JSON on `MCPConnection.authConfig`.
 */
export const MCPOAuthConfigSchema = z
    .object({
    authorizationUrl: z.string().url().optional(),
    tokenUrl: z.string().url().optional(),
    scopes: z.array(z.string()).optional(),
    /**
     * Extra provider-specific query params for the authorize request, for
     * providers that gate the refresh token behind a param rather than the
     * `offline_access` scope (e.g. Google `access_type=offline`, `prompt=consent`).
     */
    authorizationParams: z.record(z.string()).optional(),
    /** Header names this integration supports as `customHeaders` on the secret. */
    customHeaderNames: z.array(z.string()).optional(),
})
    .passthrough();
/**
 * Cached tool schemas for an MCP connection. Populated by hitting
 * `POST /v1/mcp-connections/:id/test-connection`, which opens the
 * transport, calls `tools/list`, and writes the result here. The FE reads
 * this to drive tool-picker dropdowns (e.g. on `mcp-query` nodes) so it
 * doesn't have to hit the MCP server itself.
 */
export const MCPToolSchemaCacheSchema = z.object({
    fetchedAt: z.string().datetime(),
    schemas: z.record(z.string(), z.object({
        name: z.string(),
        /** MCP servers may or may not surface a JSON Schema per tool. */
        inputSchema: z.unknown().nullable(),
        /**
         * Formant tool-group taxonomy (kg / eval / workflow / …), read from
         * the upstream server's `tools/list` `_meta.group` field. Present only
         * for Formant-owned MCPs; third-party servers won't set it. Powers the
         * console's grouped-checkbox UI on the agent-detail Tools panel
         * (DEV-16686 / DEV-16699).
         */
        group: z.string().optional(),
    })),
});
export const MCPConnectionSchema = z.object({
    id: z.string(),
    /** null = catalog/root-level row, shared and immutable to non-admins. */
    orgId: z.string().nullable(),
    ownerSubject: z.string().nullable(),
    name: z.string(),
    description: z.string().nullable(),
    endpoint: z.string(),
    transport: MCPTransportSchema,
    authType: MCPAuthTypeSchema,
    authConfig: MCPOAuthConfigSchema.nullable(),
    /**
     * Well-known connection slug (e.g. `formant_telemetry`). Set on catalog
     * rows and on org-owned rows that override a catalog category (an org row
     * with the same category wins the category-based tool routing).
     */
    category: z.string().nullable(),
    /** `null` until someone runs `POST :id/test-connection`. */
    toolSchemaCache: MCPToolSchemaCacheSchema.nullable(),
    /**
     * Derived BE-side: `true` when this org has stored credentials for this
     * connection. Lets the UI show an "authenticate" CTA without leaking
     * any secret material. For `authType === 'none'` rows this is always
     * `false` but `canTest` / `canQuery` are still `true`.
     */
    hasKeyring: z.boolean(),
    /**
     * Re-auth marker, derived BE-side from the `authError` key the refresh path
     * stamps on the row's `authConfig` when a refresh endpoint definitively
     * rejects the stored refresh token (401, or 400 `invalid_grant`). Non-null =
     * the stored credentials are dead and the user must reconnect; the console
     * renders it as a "Reconnect required" badge instead of "Connected".
     */
    authError: z.string().nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
});
export const CreateMCPConnectionInputSchema = z.object({
    name: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
    endpoint: z.string().url(),
    transport: MCPTransportSchema,
    authType: MCPAuthTypeSchema.default('none'),
    authConfig: MCPOAuthConfigSchema.optional(),
});
export const UpdateMCPConnectionInputSchema = CreateMCPConnectionInputSchema.partial().extend({
    /**
     * Opt-in rename propagation (DEV-16944). `toolRefs` reference MCP
     * connections by NAME, so a bare rename orphans every referent. Default
     * (`false` / absent): a rename of an in-use connection is refused with
     * 409 `CONNECTION_NAME_IN_USE`. When `true`, the rename goes through and
     * every reference — Agent `toolRefs` + `toolAllowlists` keys, `llm-agent`
     * WorkflowNode configs, and `WorkflowVersion.graph` snapshots — is rewritten
     * to the new name in the same transaction. The response then carries a
     * `propagated` count summary.
     */
    propagateRefs: z.boolean().optional(),
});
/**
 * Inputs for the raw MCP-spec inspection endpoints (tools/call, prompts/get).
 * Mirrors the spec's `params` shape — we accept arbitrary JSON for arguments
 * because the schema is per-tool / per-prompt and lives on the remote server.
 */
export const CallToolInputSchema = z.object({
    arguments: z.record(z.unknown()).optional(),
});
export const GetPromptInputSchema = z.object({
    arguments: z.record(z.string()).optional(),
});
/**
 * Org-authored expected output JSON Schema for a single tool. Deliberately
 * separate from `MCPToolSchemaCache` (which is wholesale-overwritten by every
 * `test-connection` call) so an annotation survives the next live re-fetch.
 * Org-scoped even on catalog connections — two orgs sharing a catalog MCP may
 * disagree about a tool's output shape.
 */
export const MCPToolAnnotationSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    mcpConnectionId: z.string(),
    toolName: z.string(),
    /** Arbitrary JSON Schema document — shape isn't validated server-side. */
    outputSchema: z.unknown(),
    createdAt: z.date(),
    updatedAt: z.date(),
});
export const UpsertMCPToolAnnotationInputSchema = z.object({
    outputSchema: z.unknown(),
});
//# sourceMappingURL=mcp.js.map